/**
 * List Toolbar Component
 * Provides search input and refresh button for list pages
 */
import { Search, RefreshCw } from 'lucide-react'
import type { JSX, ReactNode } from 'react'

interface ListToolbarProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  onRefresh: () => void
  allButtonLabel: string
  placeholder?: string
  /** Accessible name for the search input. */
  ariaLabel?: string
  loading?: boolean
  children?: ReactNode
}

export default function ListToolbar({
  searchQuery,
  onSearchChange,
  onRefresh,
  allButtonLabel,
  placeholder = "Search...",
  ariaLabel,
  loading = false,
  children
}: ListToolbarProps): JSX.Element {
  return (
    <div className="bg-white rounded-md border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="px-4 py-2 rounded-md text-sm font-medium bg-[#3454cf] text-white"
        >
          {allButtonLabel}
        </button>
        <div className="relative w-72">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={placeholder}
            {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3454cf] focus:border-transparent"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRefresh}
          title="Refresh"
          className="h-9 w-9 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {children}
      </div>
    </div>
  )
}
